import React, { useRef, useState, useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import { fileStorageService } from '../../services/FileStorageService';
import { supabaseFlowStorageService as flowStorageService } from '../../services/SupabaseFlowStorageService';

export interface ImageData {
  url: string;
  file: File;
  width: number;
  height: number;
}

/**
 * Fallback height of the empty drop zone, used when the caller does not know
 * how much room it has. Deliberately small — it is a floor for callers like
 * PagedOutput that render inside a content-sized box.
 */
export const DEFAULT_EMPTY_ZONE_HEIGHT = 160;

interface ImageUploadZoneProps {
  onImageUpload: (imageData: ImageData) => void;
  onUploadStateChange?: (uploading: boolean) => void;
  currentImage?: string;
  disabled?: boolean;
  /**
   * How tall the EMPTY drop zone should be, in px.
   *
   * The whole `height: 100%` chain above this component degrades to `auto`
   * (`.react-flow__node` is `height: auto !important`), so nothing here can
   * "fill" its parent — this zone's own height is what the node ends up
   * painting. A caller that knows the box it is supposed to occupy passes it
   * here; everyone else gets {@link DEFAULT_EMPTY_ZONE_HEIGHT}.
   *
   * Ignored once an image is present: the filled branch is unchanged.
   */
  emptyHeight?: number;
}

export const ImageUploadZone: React.FC<ImageUploadZoneProps> = ({
  onImageUpload,
  onUploadStateChange,
  currentImage,
  disabled = false,
  emptyHeight = DEFAULT_EMPTY_ZONE_HEIGHT,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [mediaRetryCount, setMediaRetryCount] = useState(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const MAX_RETRIES = 3;

  // Reset retry state when image changes
  useEffect(() => {
    setMediaRetryCount(0);
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
  }, [currentImage]);

  // Handle click to upload
  const handleClick = () => {
    // Only open file browser if there's no current image
    if (!disabled && !currentImage && !isUploading) {
      fileInputRef.current?.click();
    }
  };

  // Handle file selection
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && (file.type.startsWith('image/') || file.type.startsWith('video/'))) {
      processImageFile(file);
    }
  };

  // Helper: detect video URLs by extension
  const isVideoUrl = (url: string) => /\.(mp4|webm|mov|avi|mkv|m4v)(\?|$)/i.test(url) ||
    /\.(mp4|webm|mov|avi|mkv|m4v)(&|$)/i.test(decodeURIComponent(url));

  // Process image or video file: upload to R2 and get dimensions
  const processImageFile = async (file: File) => {
    setIsUploading(true);
    onUploadStateChange?.(true);
    const isVideo = file.type.startsWith('video/');

    try {
      const previewUrl = URL.createObjectURL(file);
      let dimensions: { width: number; height: number };

      if (isVideo) {
        // Get video dimensions using a temporary video element
        dimensions = await new Promise<{ width: number; height: number }>((resolve) => {
          const video = document.createElement('video');
          video.onloadedmetadata = () => {
            resolve({ width: video.videoWidth || 640, height: video.videoHeight || 360 });
          };
          video.onerror = () => {
            resolve({ width: 640, height: 360 }); // fallback dimensions
          };
          video.src = previewUrl;
        });
      } else {
        // Get image dimensions using a temporary image element
        dimensions = await new Promise<{ width: number; height: number }>((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            resolve({ width: img.naturalWidth, height: img.naturalHeight });
          };
          img.onerror = () => {
            reject(new Error('Failed to load image'));
          };
          img.src = previewUrl;
        });
      }

      // Show blob preview immediately while uploading
      onImageUpload({
        url: previewUrl,
        file,
        width: dimensions.width,
        height: dimensions.height,
      });

      // Upload to R2 for permanent storage (in background). Flow-scoped so
      // the upload shows up in this flow's asset gallery — without the
      // scope it lands in the global prefix and is invisible to FilePanel.
      const currentFlowId = flowStorageService.getCurrentFlowId();
      const fileInfo = await fileStorageService.uploadFile(file, {
        folder: 'image-panel',
        isPublic: true,
        scope: currentFlowId ? 'flow' : undefined,
        flowId: currentFlowId || undefined,
      });

      console.log('[ImageUploadZone] Uploaded to R2:', fileInfo.url);

      // Update with permanent R2 URL, revoke blob
      URL.revokeObjectURL(previewUrl);
      onImageUpload({
        url: fileInfo.url,
        file,
        width: dimensions.width,
        height: dimensions.height,
      });
    } catch (error) {
      console.error('[ImageUploadZone] Failed to upload media:', error);
      // Fallback to blob URL if R2 upload fails (for offline/dev scenarios)
      const url = URL.createObjectURL(file);
      if (isVideo) {
        onImageUpload({ url, file, width: 640, height: 360 });
      } else {
        const img = new Image();
        img.onload = () => {
          onImageUpload({ url, file, width: img.naturalWidth, height: img.naturalHeight });
        };
        img.src = url;
      }
    } finally {
      setIsUploading(false);
      onUploadStateChange?.(false);
    }
  };

  // Handle drag events
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !isUploading) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled || isUploading) return;

    // First, try to handle native file drops
    const file = e.dataTransfer.files[0];
    if (file && (file.type.startsWith('image/') || file.type.startsWith('video/'))) {
      processImageFile(file);
      return;
    }

    // Then, try to handle FilePanel JSON drops
    try {
      const jsonData = e.dataTransfer.getData('application/json');
      if (jsonData) {
        const dragData = JSON.parse(jsonData);
        console.log('[ImageUploadZone] FilePanel drop data:', dragData);
        
        // Check if this is an image from FilePanel
        if (dragData.isImage && (dragData.fileUrl || dragData.previewUrl)) {
          const imageUrl = dragData.fileUrl || dragData.previewUrl;
          console.log('[ImageUploadZone] Loading image from FilePanel:', imageUrl);
          
          // Load the image to get dimensions and create ImageData
          const img = new Image();
          img.crossOrigin = 'anonymous';
          
          img.onload = async () => {
            try {
              // Fetch the image and create a File object
              const response = await fetch(imageUrl);
              const blob = await response.blob();
              const fileName = dragData.fileName || 'dropped-image.png';
              const imageFile = new File([blob], fileName, { type: blob.type || 'image/png' });
              
              onImageUpload({
                url: imageUrl,
                file: imageFile,
                width: img.naturalWidth,
                height: img.naturalHeight,
              });
            } catch (err) {
              console.error('[ImageUploadZone] Failed to fetch image:', err);
              // Fallback: use the URL directly without file
              onImageUpload({
                url: imageUrl,
                file: new File([], dragData.fileName || 'image.png', { type: 'image/png' }),
                width: img.naturalWidth,
                height: img.naturalHeight,
              });
            }
          };
          
          img.onerror = (err) => {
            console.error('[ImageUploadZone] Failed to load image:', err);
          };
          
          img.src = imageUrl;
        }
      }
    } catch (err) {
      console.log('[ImageUploadZone] Not a JSON drop, ignoring');
    }
  };

  // Handle paste events
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      if (disabled || isUploading) return;

      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith('image/') || item.type.startsWith('video/')) {
          const file = item.getAsFile();
          if (file) {
            processImageFile(file);
            e.preventDefault();
            break;
          }
        }
      }
    };

    const container = containerRef.current;
    if (container) {
      // Add paste listener to container when focused
      container.addEventListener('paste', handlePaste as any);
      return () => {
        container.removeEventListener('paste', handlePaste as any);
      };
    }
    
    return undefined;
  }, [disabled, isUploading]);

  return (
    <Box
      ref={containerRef}
      tabIndex={0}
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        width: '100%',
        height: currentImage ? '100%' : 'auto',
        minHeight: currentImage ? 'auto' : `${emptyHeight}px`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: disabled ? 'default' : (currentImage ? 'pointer' : 'pointer'),
        border: currentImage ? 'none' : (isDragging ? '2px dashed var(--color-accent-primary)' : '2px dashed var(--color-border)'),
        borderRadius: currentImage ? '0' : '8px',
        backgroundColor: isDragging ? 'rgba(59, 130, 246, 0.08)' : 'transparent',
        transition: 'all 0.2s ease',
        position: 'relative',
        outline: 'none',
        '&:hover': disabled || currentImage ? {} : {
          borderColor: 'var(--color-accent-primary)',
          backgroundColor: 'rgba(59, 130, 246, 0.04)',
        },
        '&:focus': currentImage ? {} : {
          borderColor: 'var(--color-accent-primary)',
        },
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {currentImage ? (
        isVideoUrl(currentImage) ? (
          <Box
            component="video"
            src={mediaRetryCount > 0 ? `${currentImage}${currentImage.includes('?') ? '&' : '?'}_r=${mediaRetryCount}` : currentImage}
            controls
            onLoadedData={() => setMediaRetryCount(0)}
            onError={() => {
              if (mediaRetryCount < MAX_RETRIES) {
                const delay = Math.min(1000 * Math.pow(2, mediaRetryCount), 4000);
                retryTimerRef.current = setTimeout(() => setMediaRetryCount(c => c + 1), delay);
              }
            }}
            sx={{
              width: '100%',
              height: '100%',
              display: 'block',
              borderRadius: '4px',
              objectFit: 'contain',
            }}
          />
        ) : (
        <Box
          component="img"
          src={mediaRetryCount > 0 ? `${currentImage}${currentImage.includes('?') ? '&' : '?'}_r=${mediaRetryCount}` : currentImage}
          alt="Uploaded"
          onLoad={() => setMediaRetryCount(0)}
          onError={() => {
            if (mediaRetryCount < MAX_RETRIES) {
              const delay = Math.min(1000 * Math.pow(2, mediaRetryCount), 4000);
              retryTimerRef.current = setTimeout(() => setMediaRetryCount(c => c + 1), delay);
            }
          }}
          sx={{
            width: '100%',
            height: '100%',
            display: 'block',
            borderRadius: '4px',
            objectFit: 'contain',
          }}
        />
        )
      ) : isUploading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={32} />
          <Typography variant="body2" color="text.secondary">
            Uploading...
          </Typography>
        </Box>
      ) : (
        <>
          <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 36, mb: 1, color: 'var(--color-text-tertiary)' }} />
          <Typography variant="body2" sx={{ mb: 0.5, color: 'var(--color-text-secondary)', fontSize: '13px', fontWeight: 500 }}>
            Click to upload
          </Typography>
          <Typography variant="caption" sx={{ color: 'var(--color-text-tertiary)', fontSize: '11px', textAlign: 'center', px: 1 }}>
            or drop • or paste (Ctrl+V)<br/>
            Images &amp; video supported
          </Typography>
        </>
      )}
    </Box>
  );
};
